import React from 'react';
import ErrorBoundary from './ErrorBoundary';
import useAsyncData from '../hooks/useAsyncData';
import useStaleWarning from '../hooks/useStaleWarning';
import useAutoRefresh from '../hooks/useAutoRefresh';

const STATE_ICONS = { passed: '✅', failed: '❌', warning: '⚠️', pending: '⏳', running: '🔄' };

const EvidencePanel = () => {
  const { data: evidence, loading, error, retry } = useAsyncData('/api/evidence');
  const { isStale, lastUpdated } = useStaleWarning(evidence);
  useAutoRefresh(retry);

  const fmtTime = (d) => d ? d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '–';

  if (loading) {
    return (
      <ErrorBoundary>
        <section className="panel evidence-panel" aria-label="Evidence Panel" aria-busy="true">
          <h2>Verification</h2>
          <div className="skeleton">
            <div className="skeleton-line" />
            <div className="skeleton-line" />
          </div>
        </section>
      </ErrorBoundary>
    );
  }
  if (error) {
    return (
      <ErrorBoundary>
        <section className="panel evidence-panel" aria-label="Evidence Panel" aria-live="assertive">
          <h2>Verification</h2>
          <p className="error-msg">Failed to load evidence.</p>
          <button type="button" className="btn-retry" onClick={retry}>Retry</button>
        </section>
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary>
      <section className="panel evidence-panel" aria-label="Evidence Panel" aria-busy="false">
        <div className="panel-header">
          <h2>Verification</h2>
          <div className="panel-meta">
            <span className="last-updated" aria-label="Last updated">
              Last updated: {fmtTime(lastUpdated)}
            </span>
            {isStale && (
              <span className="badge badge-stale" role="status">Stale</span>
            )}
            <button type="button" className="btn-icon btn-refresh" onClick={retry} aria-label="Refresh evidence">
              ↻
            </button>
          </div>
        </div>
        <ul className="evidence-list" aria-label="Evidence items">
          {(evidence || []).length === 0 && <li className="empty">No verification evidence</li>}
          {(evidence || []).map((item) => {
            const icon = STATE_ICONS[item.state] || '⚪';
            return (
              <li className="evidence-item" key={item.name}>
                <strong>{icon} {item.name}</strong>
                <span className="evidence-state" aria-label={`State: ${item.state}`}>{item.state}</span>
                <p className="evidence-detail">{item.detail}</p>
              </li>
            );
          })}
        </ul>
      </section>
    </ErrorBoundary>
  );
};

export default EvidencePanel;
